
import React, { useEffect, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import DownloadTemplate from "../modals/download-inlined-template";
import ShareTemplate from "../modals/share-template";
import SendPreview from "../modals/send-preview";
import LiveClientPreviewsModal from "../modals/live-client-previews-modal";
import SyncTemplateModal from "../modals/sync-template";
import PublicEditorPromptModal from "../modals/public-editor-prompt";
import toast from "react-hot-toast";
import EditorSettingModal from "../modals/editor-setting-modal"
import { Tooltip } from "./tooltip";
import Moon from "../../icons/moon";
import Previews from "../../icons/previews";
import Integrations from "../../icons/integrations";
import { Button, IconButton, cn } from "../../ui";
import { trackProductEvent } from "../../analytics";

const UPDATE = gql`
  mutation UpdateTemplate($id: ID!, $css: String, $rawTemplate: String) {
    updateTemplate(input: { id: $id, css: $css, rawTemplate: $rawTemplate }) {
      id
      name
      css
      rawTemplate
      updatedAt
    }
  }
`;

interface IProps {
  hasChanges?: boolean;
  data?: { id: string; css: string; rawTemplate: string };
  buttonRef?: any;
  cssEditorRef?: any;
  htmlEditorRef?: any;
  formatOnSave: boolean;
  saveRequestRef?: React.MutableRefObject<
    (() => Promise<boolean>) | null
  >;
}

export const Save = ({
  hasChanges = false,
  data,
  formatOnSave,
  cssEditorRef,
  htmlEditorRef,
  buttonRef,
  saveRequestRef,
}: IProps) => {
  const [isModalOpen, toggleModal] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [updateTemplate] = useMutation(UPDATE);
  const [codeFormatted, setCodeFormatted] = useState<boolean>(false)

  useEffect(() => {
    if (codeFormatted && data && formatOnSave) {
      saveTemplate()
      setCodeFormatted(false)
    }
  }, [formatOnSave, codeFormatted])

  const isPublic = data?.id === "starter";
  const saveTemplate = async () => {
    if (isPublic) {
      toggleModal(true);
      return false;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const promise = updateTemplate({
        variables: {
          id: data?.id,
          css: data?.css,
          rawTemplate: data?.rawTemplate,
        },
      });
      toast.promise(promise, {
        loading: "Saving...",
        success: "Saved!",
        error: "Error saving template.",
      });
      await promise;
      return true;
    } catch (error) {
      setFormError(error.message || "Unable to save template.");
      return false;
    } finally {
      setSubmitting(false);
    }
  };
  if (saveRequestRef) {
    saveRequestRef.current = saveTemplate;
  }
  return (
    <>
      {hasChanges ? (
        <button
          tabIndex={0}
          disabled={isSubmitting}
          className="text-[13px] font-medium inline-flex items-center text-pink hover:text-pink-dark focus:outline-none animate-pulse"
          onClick={() => {
            if (formatOnSave) {
              htmlEditorRef.current.getAction('editor.action.formatDocument').run();
              cssEditorRef.current.getAction('editor.action.formatDocument').run();
              setTimeout(() => {
                setCodeFormatted(true)
              }, 2000)
            } else {
              saveTemplate()
            }
          }}
          ref={buttonRef}
        >
          <svg
            className="mr-1.5 flex-shrink-0"
            width="14"
            height="14"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 16 16"
            stroke="none"
          >
            <path d="M16 7.442a3.878 3.878 0 0 0-1.181-2.778 3.807 3.807 0 0 0-2.298-1.056.166.166 0 0 1-.128-.08 5.122 5.122 0 0 0-9.4 1.529.167.167 0 0 1-.151.133A3.06 3.06 0 0 0 0 8.24a2.883 2.883 0 0 0 .824 2.14 3.679 3.679 0 0 0 2.403.92.666.666 0 1 0-.02-1.333 2.192 2.192 0 0 1-1.448-.533 1.583 1.583 0 0 1-.426-1.194 1.717 1.717 0 0 1 1.32-1.678c.249-.061.51-.065.761-.013a.667.667 0 0 0 .8-.613 3.79 3.79 0 0 1 7.213-1.392.641.641 0 0 0 .628.38 2.467 2.467 0 0 1 1.838.7 2.5 2.5 0 0 1 .774 1.818 2.556 2.556 0 0 1-2.292 2.525.666.666 0 1 0 .175 1.322A3.872 3.872 0 0 0 16 7.442Z" />
            <path d="M8.471 6.493a.667.667 0 0 0-.942 0L5.195 8.826a.667.667 0 0 0 .472 1.14h1.166a.166.166 0 0 1 .167.167v4.834a1 1 0 0 0 2 0v-4.834a.166.166 0 0 1 .167-.166h1.166a.667.667 0 0 0 .472-1.138L8.47 6.493Z" />
          </svg>{" "}
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      ) : (
        <button
          className="text-[13px] font-medium inline-flex items-center text-green-600 dark:text-green-400 focus:outline-none"
          disabled
        >
          <svg
            className="mr-1.5 flex-shrink-0"
            width="14"
            height="14"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 16 16"
            stroke="none"
          >
            <path d="M16 7.909a3.843 3.843 0 0 0-3.477-3.968.167.167 0 0 1-.128-.08 5.123 5.123 0 0 0-9.4 1.529.167.167 0 0 1-.151.133A3.095 3.095 0 0 0 .416 7.016 2.98 2.98 0 0 0 0 8.649a2.862 2.862 0 0 0 .823 2.066c.337.323.743.565 1.187.708a.643.643 0 0 0 .829-.573v-.017a.656.656 0 0 0-.439-.69 1.72 1.72 0 0 1-.64-.38 1.582 1.582 0 0 1-.427-1.19 1.725 1.725 0 0 1 2.081-1.69.66.66 0 0 0 .73-.352.667.667 0 0 0 .072-.29c.057-3.824 5.529-4.914 7.212-1.362a.65.65 0 0 0 .627.38 2.489 2.489 0 0 1 2 .872c.44.522.655 1.195.6 1.875a2.284 2.284 0 0 1-.982 1.778.65.65 0 0 0-.266.643l.006.039a.646.646 0 0 0 1.017.405A3.6 3.6 0 0 0 16 7.91Z" /><path d="M8.154 6.967a4.333 4.333 0 1 0 0 8.666 4.333 4.333 0 0 0 0-8.666Zm2.083 2.77a.5.5 0 0 1 .1.7L8.4 13.017a.996.996 0 0 1-.733.4h-.074a.99.99 0 0 1-.704-.296l-1-1a.501.501 0 0 1 .711-.706l.867.867a.166.166 0 0 0 .25-.018l1.82-2.43a.5.5 0 0 1 .7-.1v.003Z" />
          </svg>{" "}
          Saved
        </button>
      )}
      {formError && (
        <p className="text-[13px] text-pink items-center inline-flex">Error: {formError}</p>
      )}
      {isPublic && (
        <PublicEditorPromptModal
          isOpen={isModalOpen}
          toggle={() => toggleModal(false)}
          rawTemplate={data?.rawTemplate}
          css={data?.css}
        />
      )}
    </>
  );
};

export const Download = ({
  data,
  isPublic,
  plan,
}: {
  hasChanges?: boolean;
  data?: { css: string; rawTemplate: string; name: string; id?: string };
  isPublic?: boolean;
  plan?: string;
}) => {
  const [isModalOpen, toggleModal] = useState(false);
  const handleClick = () => toggleModal(true);

  return (
    <>
      <Tooltip label="Download/Export HTML">
        <IconButton
          tabIndex={0}
          onClick={handleClick}
          id="btn-downloadexport"
        >
          <svg
            className="flex-shrink-0"
            width="14"
            height="14"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
          >
            <g clipPath="url(#clip0)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.086.379a1.293 1.293 0 012.207.914v6.044a.167.167 0 00.167.166h1.772a.646.646 0 01.489 1.071l-3.233 3.717a.646.646 0 01-.976 0L4.28 8.574a.647.647 0 01.488-1.07h1.773a.167.167 0 00.167-.167V1.293c0-.343.136-.672.378-.914zm7.454 11.15a.808.808 0 01.882-.177.81.81 0 01.501.748v1.237a2.667 2.667 0 01-2.667 2.666H2.744a2.666 2.666 0 01-2.663-2.666V12.1a.808.808 0 111.616 0v1.237a1.048 1.048 0 001.047 1.046h10.512a1.048 1.048 0 001.048-1.046V12.1c0-.214.085-.42.236-.571z"
              />
            </g>
          </svg>
          <span className="hidden xl:inline ml-1.5">Export</span>
        </IconButton>
      </Tooltip>
      {isPublic ? (
        <PublicEditorPromptModal
          isOpen={isModalOpen}
          toggle={() => toggleModal(false)}
          rawTemplate={data?.rawTemplate}
          css={data?.css}
        />
      ) : (
        <DownloadTemplate
          isOpen={isModalOpen}
          toggle={() => toggleModal(false)}
          data={data}
          plan={plan}
        />
      )}
    </>
  );
};

export const EditorSettingOptions = ({ formError, intialValues, setSettingData, submitData, htmlEditorRef, cssEditorRef }) => {
  const [isModalOpen, toggleModal] = useState(false);
  const handleChangeInput = (key: string, val: string | number | boolean) => {
    submitData({
      ...intialValues,
      [key]: val
    })
    setSettingData({
      ...intialValues,
      [key]: val
    })
  }

  const handleClick = () => { !formError && toggleModal((prev) => !prev) }

  return (
    <>
      <Tooltip label="Editor Settings">
        <IconButton
          tabIndex={0}
          id="btn-editorsettings"
          onClick={handleClick}
        >
          <svg className="flex-shrink-0" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0)">
              <path fillRule="evenodd" clipRule="evenodd" d="M1.305 0h13.39C15.415 0 16 .585 16 1.305v13.391A1.306 1.306 0 0 1 14.695 16H1.305C.585 16 0 15.416 0 14.696V1.306C0 .584.584 0 1.305 0Zm.362 14.694 12.666-.027c.184 0 .334-.15.334-.334V1.64a.333.333 0 0 0-.334-.333L1.64 1.333a.333.333 0 0 0-.334.334l.028 12.694c0 .184.15.333.334.333Z" />
              <path fillRule="evenodd" clipRule="evenodd" d="M7.43 11.667H3a.667.667 0 0 1 0-1.334h4.43a2.333 2.333 0 0 1 4.473 0H13a.667.667 0 0 1 0 1.334h-1.097a2.333 2.333 0 0 1-4.472 0ZM10.668 11a1 1 0 1 0-2 0 1 1 0 0 0 2 0ZM3.764 5.667H3a.667.667 0 1 1 0-1.334h.764a2.333 2.333 0 0 1 4.472 0H13a.667.667 0 1 1 0 1.334H8.236a2.333 2.333 0 0 1-4.472 0ZM7 5a1 1 0 1 0-2 0 1 1 0 0 0 2 0Z" />
            </g>
          </svg>
          <span className="hidden xl:inline ml-1.5">Settings</span>
        </IconButton>
      </Tooltip>
      <EditorSettingModal formError={formError} intialValues={intialValues} htmlEditorRef={htmlEditorRef} cssEditorRef={cssEditorRef} toggle={handleClick} isOpen={isModalOpen} handleChangeInput={handleChangeInput} />
    </>
  );
};

export const DarkTheme = ({ darkTheme, setDarkTheme }) => {
  const handleChangeDarkMode = () => {
    setDarkTheme((prev) => !prev);
  };

  const isOn = darkTheme;

  return (
      <Tooltip label="Dark Mode Preview (Simulated)">
        <IconButton
          className={cn(
            isOn ? 'text-green-600 hover:text-green-600 dark:text-green-400 dark:hover:text-green-400' : undefined
          )}
          tabIndex={0}
          onClick={handleChangeDarkMode}
          id="btn-dark-mode-preview"
          aria-label="Dark Mode Preview (Simulated)"
        >
        <Moon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="hidden xl:inline ml-1.5">Dark Mode</span>
        </IconButton>
      </Tooltip>
  );
};

export const InspectElement = ({ inspectMode, setInspectMode }) => {
  const handleToggle = () => {
    setInspectMode((prev) => {
      const nextInspectMode = !prev;
      trackProductEvent("inspect", { enabled: nextInspectMode });
      return nextInspectMode;
    });
  };

  const isOn = inspectMode;

  return (
      <Tooltip label="Inspect Element">
        <IconButton
          className={cn(
            isOn ? 'text-green-600 hover:text-green-600 dark:text-green-400 dark:hover:text-green-400' : undefined
          )}
          tabIndex={0}
          onClick={handleToggle}
          id="btn-inspect-element"
          aria-label="Inspect Element"
        >
        <svg
          className="h-3.5 w-3.5 flex-shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          fill="none"
          viewBox="0 0 16 16"
        >
          <g fill="currentColor" clipPath="url(#inspect-icon-clip)">
            <path d="M2 .009H.667A.667.667 0 0 0 0 .675V2.01a.667.667 0 1 0 1.333 0v-.5a.167.167 0 0 1 .167-.167H2A.667.667 0 0 0 2 .009M12.667 1.342h.5a.166.166 0 0 1 .166.167v.5a.667.667 0 0 0 1.334 0V.675A.667.667 0 0 0 14 .01h-1.333a.667.667 0 1 0 0 1.333M6 .009H4.667a.667.667 0 0 0 0 1.333H6A.667.667 0 0 0 6 .009M10 1.342a.667.667 0 1 0 0-1.333H8.667a.667.667 0 0 0 0 1.333zM2 13.342h-.5a.167.167 0 0 1-.167-.167v-.5a.667.667 0 0 0-1.333 0v1.334a.667.667 0 0 0 .667.666H2a.667.667 0 1 0 0-1.333M6 13.342H4.667a.666.666 0 1 0 0 1.333H6a.667.667 0 1 0 0-1.333M.667 6.675a.667.667 0 0 0 .666-.666V4.675a.667.667 0 1 0-1.333 0V6.01a.667.667 0 0 0 .667.666M.667 10.675a.667.667 0 0 0 .666-.666V8.675a.667.667 0 1 0-1.333 0v1.334a.667.667 0 0 0 .667.666M14 4.009a.667.667 0 0 0-.667.666V6.01a.667.667 0 1 0 1.334 0V4.675A.666.666 0 0 0 14 4.01M15.659 10.728 8.74 8.133a.48.48 0 0 0-.539.086.48.48 0 0 0-.083.539l2.62 6.891a.45.45 0 0 0 .417.339h.024c.09-.006.31-.055.393-.39l.761-3.126a.17.17 0 0 1 .122-.122l3.155-.789c.333-.083.383-.304.389-.395a.44.44 0 0 0-.34-.438" />
          </g>
          <defs>
            <clipPath id="inspect-icon-clip">
              <path fill="#fff" d="M0 0h16v16H0z" />
            </clipPath>
          </defs>
        </svg>
          <span className="hidden xl:inline ml-1.5">Inspect</span>
        </IconButton>
      </Tooltip>
  );
};

export const Share = ({
  data,
}: {
  data?: { id: string; rawTemplate?: string; css?: string };
}) => {
  const [isModalOpen, toggleModal] = useState(false);
  const isPublic = data?.id === "starter";
  const handleClick = () => {
    return toggleModal(true);
  };

  return (
    <>
      <Tooltip label="Share Template">
        <IconButton
          tabIndex={0}
          onClick={handleClick}
          id="btn-share"
        >
          <svg
            className="flex-shrink-0"
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.745.175a.333.333 0 01.17.102l3 3.334a.333.333 0 010 .446l-3 3.333a.333.333 0 01-.581-.223V6a.167.167 0 00-.167-.167h-3.66a2.77 2.77 0 00-2.626 1.926.333.333 0 01-.6.082 3.825 3.825 0 01-.614-2.094 3.921 3.921 0 013.92-3.914h3.58a.166.166 0 00.167-.166V.499a.333.333 0 01.411-.324zM10.05 6.881a.167.167 0 01.118-.048h1a.166.166 0 01.166.167v7.498A1.333 1.333 0 0110 15.832H1.333A1.333 1.333 0 010 14.498V5.832a1.333 1.333 0 011.333-1.333H3.62a.167.167 0 01.163.203 4.62 4.62 0 00-.115.965.167.167 0 01-.165.166H1.333v8.666H10V7c0-.044.018-.086.049-.118z"
              />
            </g>
          </svg>
          <span className="hidden xl:inline ml-1.5">Share</span>
        </IconButton>
      </Tooltip>
      {isPublic ? (
        <PublicEditorPromptModal
          isOpen={isModalOpen}
          toggle={() => toggleModal(false)}
          rawTemplate={data?.rawTemplate}
          css={data?.css}
        />
      ) : (
        <ShareTemplate
          isOpen={isModalOpen}
          toggle={() => toggleModal(false)}
          id={data?.id}
        />
      )}
    </>
  );
};

export const Send = ({
  data,
  account,
  onOpenLivePreviews,
}: {
  hasChanges?: boolean;
  data?: { id: string; rawTemplate: string; css: string; subject?: string };
  account?: {
    plan?: string;
    maxLivePreviewCredits?: number;
  };
  onOpenLivePreviews?: () => void;
}) => {
  const [isModalOpen, toggleModal] = useState(false);
  const isPublic = data?.id === "starter";
  const handleClick = () => {
    return toggleModal(true);
  };

  return (
    <>
      <Button
        tag="button"
        tabIndex={0}
        onClick={handleClick}
        id="btn-sendtest"
      >
        <svg
          className="mr-1 fill-current text-white"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.0234 0.0833202C15.2574 -0.0468849 15.5432 -0.0207295 15.7517 0.149987C15.959 0.319497 16.0472 0.602683 15.9751 0.866654L12.5805 13.3593C12.5013 13.6477 12.3065 13.8865 12.0461 14.0147C11.7839 14.1438 11.4813 14.1504 11.2141 14.0327L9.19367 13.14C9.13102 13.112 9.05822 13.1286 9.01255 13.1813C8.62215 13.632 6.86151 15.6607 6.83655 15.684C6.62223 15.885 6.34475 15.9974 6.05639 16C5.56803 16.0031 5.12983 15.6881 4.95943 15.2113L3.35943 10.714C3.34122 10.6616 3.3319 10.6063 3.33191 10.5507L1.24935 9.63065C0.894204 9.4774 0.656318 9.12387 0.639174 8.72383C0.622029 8.3238 0.828726 7.94959 1.16935 7.76399L15.0234 0.0833202ZM10.5792 5.32199L4.37635 10.6973L5.66915 14.3253L6.07427 11.692C6.08703 11.606 6.12173 11.5252 6.17475 11.458L10.8071 5.55465C10.8366 5.52511 10.8522 5.48362 10.8499 5.44107C10.8475 5.39852 10.8274 5.35916 10.7949 5.33332C10.7691 5.29984 10.7307 5.27946 10.6895 5.2773C10.6483 5.27513 10.6082 5.29139 10.5792 5.32199Z"
          />
        </svg>{" "}
        Send Test
      </Button>
      {isPublic ? (
        <PublicEditorPromptModal
          isOpen={isModalOpen}
          toggle={() => toggleModal(false)}
          rawTemplate={data?.rawTemplate}
          css={data?.css}
        />
      ) : (
        <SendPreview
          isOpen={isModalOpen}
          toggle={() => toggleModal(false)}
          data={data}
          account={account}
          onOpenLivePreviews={onOpenLivePreviews}
        />
      )}
    </>
  );
};

export const SyncToEsp = ({
  templateId,
  hasUnsavedChanges,
  onRequestSave,
}: {
  templateId: string;
  hasUnsavedChanges: boolean;
  onRequestSave: () => Promise<boolean>;
}) => {
  const [isModalOpen, toggleModal] = useState(false);

  return (
    <>
      <Tooltip label="Sync to ESP">
        <IconButton
          tabIndex={0}
          onClick={() => toggleModal(true)}
          id="btn-sync-esp"
          aria-label="Sync to ESP"
        >
          <Integrations className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="hidden xl:inline ml-1.5">Sync to ESP</span>
        </IconButton>
      </Tooltip>
      <SyncTemplateModal
        isOpen={isModalOpen}
        toggle={() => toggleModal(false)}
        templateId={templateId}
        hasUnsavedChanges={hasUnsavedChanges}
        onRequestSave={onRequestSave}
      />
    </>
  );
};

export const LivePreviews = ({
  data,
  account,
  isOpen,
  onOpen,
  onClose,
}: {
  data?: { id: string; name: string; rawTemplate: string; css: string };
  account?: {
    plan?: string;
    livePreviewCount?: number;
    maxLivePreviewCredits?: number;
  };
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}) => {
  const [isModalOpen, toggleModal] = useState(false);
  const isPublic = data?.id === "starter";
  const isControlled = onOpen != null && onClose != null;
  const modalIsOpen = isControlled ? !!isOpen : isModalOpen;
  const openModal = () => {
    if (isControlled) {
      onOpen();
      return;
    }
    toggleModal(true);
  };
  const closeModal = () => {
    if (isControlled) {
      onClose();
      return;
    }
    toggleModal(false);
  };

  return (
    <>
      <Tooltip label="Live Client Previews">
        <IconButton
          tabIndex={0}
          onClick={openModal}
          id="btn-live-client-previews"
          aria-label="Live Client Previews"
        >
          <Previews className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="hidden xl:inline ml-1.5">Live Previews</span>
        </IconButton>
      </Tooltip>
      {isPublic ? (
        <PublicEditorPromptModal
          isOpen={modalIsOpen}
          toggle={closeModal}
          rawTemplate={data?.rawTemplate}
          css={data?.css}
        />
      ) : (
        <LiveClientPreviewsModal
          isOpen={modalIsOpen}
          toggle={closeModal}
          data={data}
          account={account}
        />
      )}
    </>
  );
};
